import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { runHistorianQuery } from '@utils/historianChart/historianChartService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface HistoricalDataPoint {
  timestamp: string;
  value: number | null; // null for missing data gaps in chart
}

export interface HistorianApiResponseItem {
  _time?: string;
  timestamp?: string;
  [dynamicField: string]: unknown; // For dynamic fields like rack_{unitId}_{rackId}_MaxCellT_max
}

export interface TimeInterval {
  value: number;
  unit: 'second' | 'minute' | 'hour' | 'day';
}

export interface UseHistoricalDataParams {
  siteId: string;
  unitId: string;
  rackId: string;
  dateFrom?: string;
  dateTo?: string;
  interval?: TimeInterval;
  enabled?: boolean;
}

export type UseHistoricalDataReturn = UseQueryResult<
  HistoricalDataPoint[],
  Error
> & {
  isFetching: boolean;
};

const DEFAULT_SITE_ID = '0000000A';
const DEFAULT_HOURS_BACK = 2;
const DEFAULT_INTERVAL: TimeInterval = {
  value: 5,
  unit: 'second',
};
export const useHistoricalData = ({
  siteId,
  unitId,
  rackId,
  dateFrom,
  dateTo,
  interval,
  enabled = true,
}: UseHistoricalDataParams): UseHistoricalDataReturn => {
  const queryResult = useQuery<HistoricalDataPoint[], Error>({
    queryKey: [
      'historicalData',
      siteId,
      unitId,
      rackId,
      dateFrom,
      dateTo,
      interval,
    ] as const,
    queryFn: async (): Promise<HistoricalDataPoint[]> => {
      const queryDateFrom =
        dateFrom ||
        dayjs
          .utc()
          .subtract(DEFAULT_HOURS_BACK, 'hours')
          .format('YYYY-MM-DDTHH:mm:ss[Z]');
      const queryDateTo =
        dateTo || dayjs.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

      const query = {
        dateFrom: queryDateFrom,
        dateTo: queryDateTo,
        interval: interval || DEFAULT_INTERVAL,
        aggregateFunctions: ['max'],
        criteria: [
          {
            category: 'rack' as const,
            siteId: siteId || DEFAULT_SITE_ID,
            field: 'MaxCellT',
            axis: 'Y1' as const,
            unitId: unitId,
            sourceDeviceId: rackId,
            dataType: 'float' as const,
            deviceId: rackId,
          },
        ],
      };

      const response = await runHistorianQuery(query);

      if (!response || !Array.isArray(response)) {
        return [];
      }

      return transformHistorianResponse(
        response as HistorianApiResponseItem[],
        unitId,
        rackId,
      );
    },
    enabled: enabled && Boolean(unitId) && Boolean(rackId),
    retry: false,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    staleTime: 30 * 1000, // Consider data stale after 30 seconds
  });

  return {
    ...queryResult,
    isFetching: queryResult.isFetching,
  };
};

function transformHistorianResponse(
  response: HistorianApiResponseItem[],
  unitId: string,
  rackId: string,
): HistoricalDataPoint[] {
  // Dynamic field pattern: rack_{unitId}_{rackId}_MaxCellT_max
  const valueFieldName = `rack_${unitId}_${rackId}_MaxCellT_max`;

  return response.map((item: HistorianApiResponseItem) => {
    const value = extractTemperatureValue(item[valueFieldName]);
    const timestamp = parseTimestamp(item._time || item.timestamp);

    return {
      timestamp,
      value,
    };
  });
}

function extractTemperatureValue(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null; // Preserve nulls for chart gaps
  }

  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }

  return null;
}

function parseTimestamp(rawTimestamp: unknown): string {
  const timestampStr = String(rawTimestamp);
  const parsed = dayjs.utc(timestampStr);

  return parsed.isValid() ? parsed.toISOString() : dayjs.utc().toISOString(); // Fallback to current time
}
